import { Link } from 'react-router-dom';
import { ArrowRight, Target, Eye, Award, Users, Building, Scale, Shield, Briefcase, CheckCircle, Globe, Handshake } from 'lucide-react';
import { SEO } from '../components/SEO';

export const About = ({ onOpenLeadPopup }) => {
  const values = [
    {
      icon: Target,
      title: 'Excellence',
      description: 'We pursue the highest standards in every matter we handle, delivering exceptional results for our clients.'
    },
    {
      icon: Users,
      title: 'Client Focus',
      description: 'Your success is our priority. We listen, understand, and craft strategies tailored to your unique needs.'
    },
    {
      icon: Scale,
      title: 'Integrity',
      description: 'We uphold the highest ethical standards, providing honest counsel and transparent communication.'
    },
    {
      icon: Award,
      title: 'Innovation',
      description: 'We embrace new approaches and technologies to deliver efficient, cost-effective legal solutions.'
    }
  ];

  const industries = [
    { name: 'Technology & Software', icon: Globe },
    { name: 'Healthcare & Life Sciences', icon: Shield },
    { name: 'Financial Services', icon: Briefcase },
    { name: 'Manufacturing', icon: Building },
    { name: 'Real Estate', icon: Building },
    { name: 'Non-Profit Organizations', icon: Users }
  ];

  const commitments = [
    {
      title: 'Personalized Attention',
      description: 'Every client receives dedicated attention from experienced attorneys who understand their industry and business goals.'
    },
    {
      title: 'Proactive Communication',
      description: 'We keep you informed at every stage, anticipating issues before they arise and providing timely updates on your matters.'
    },
    {
      title: 'Cost-Effective Solutions',
      description: 'We structure our services to deliver maximum value, offering flexible fee arrangements that align with your budget.'
    },
    {
      title: 'Long-Term Partnership',
      description: 'We build lasting relationships with our clients, growing alongside their businesses and adapting to their evolving needs.'
    }
  ];

  return (
    <div className="page-wrapper" data-testid="about-page">
      <SEO 
        title="About Us"
        description="Learn about Legal Emphasis, a premier San Francisco law firm providing strategic legal solutions for businesses. Our experienced attorneys deliver results in corporate law, IP, immigration, and more."
        keywords="about legal emphasis, San Francisco law firm, business attorneys, corporate lawyers, legal team"
      />
      
      {/* Hero Section */}
      <section className="bg-[#0A2A5E] py-20 md:py-32" data-testid="about-hero">
        <div className="le-container">
          <div className="max-w-3xl">
            <h1 className="le-heading-1 text-white mb-6">About Legal Emphasis</h1>
            <p className="text-xl text-gray-200 leading-relaxed">
              A full-service law firm dedicated to providing strategic legal solutions for businesses across the United States.
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="le-section bg-white" data-testid="about-intro">
        <div className="le-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="le-heading-2 mb-6">Our Firm</h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  Legal Emphasis is a premier law firm headquartered in San Francisco, California. We represent businesses of all sizes—from innovative startups to established corporations—providing the sophisticated legal counsel they need to succeed in today's competitive landscape.
                </p>
                <p>
                  Our attorneys bring extensive experience across a wide range of practice areas, including corporate law, intellectual property, government contracting, business litigation, and immigration. We combine deep legal expertise with practical business insight to deliver solutions that drive real results.
                </p>
                <p>
                  What sets us apart is our commitment to understanding each client's unique circumstances and goals. We don't offer one-size-fits-all solutions. Instead, we work closely with our clients to develop tailored strategies that address their specific needs and position them for long-term success.
                </p>
              </div>
            </div>
            <div className="relative">
              <div 
                className="w-full h-[400px] bg-cover bg-center"
                style={{
                  backgroundImage: `url('https://images.unsplash.com/photo-1758599543152-a73184816eba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxhdHRvcm5leSUyMGhhbmRzaGFrZSUyMGNsaWVudCUyMGFncmVlbWVudCUyMHN1Y2Nlc3MlMjBwcm9mZXNzaW9uYWx8ZW58MHx8fHwxNzczOTQ0MzQwfDA&ixlib=rb-4.1.0&q=85')`
                }}
              ></div>
              <div className="absolute -bottom-6 -right-6 bg-[#0A2A5E] p-6 hidden lg:block">
                <Building className="text-white" size={48} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="le-section bg-[#F8F9FA]" data-testid="about-mission">
        <div className="le-container">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="bg-white p-10 border border-[#E2E8F0]">
              <div className="w-14 h-14 bg-[#0A2A5E] flex items-center justify-center mb-6">
                <Target className="text-white" size={28} />
              </div>
              <h3 className="text-2xl font-semibold text-[#0A2A5E] mb-4">Our Mission</h3>
              <p className="text-gray-600 leading-relaxed">
                To provide exceptional legal services that empower businesses to achieve their goals. We are committed to delivering strategic, cost-effective solutions while maintaining the highest standards of professionalism and ethical conduct.
              </p>
            </div>
            <div className="bg-white p-10 border border-[#E2E8F0]">
              <div className="w-14 h-14 bg-[#0A2A5E] flex items-center justify-center mb-6">
                <Eye className="text-white" size={28} />
              </div>
              <h3 className="text-2xl font-semibold text-[#0A2A5E] mb-4">Our Vision</h3>
              <p className="text-gray-600 leading-relaxed">
                To be the trusted legal partner of choice for businesses seeking sophisticated counsel with a practical, results-oriented approach. We strive to build lasting relationships founded on trust, expertise, and shared success.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="le-section bg-white" data-testid="about-values">
        <div className="le-container">
          <div className="text-center mb-16">
            <h2 className="le-heading-2 mb-4">Our Core Values</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              The principles that guide everything we do at Legal Emphasis.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <div key={index} className="text-center" data-testid={`value-${index}`}>
                  <div className="w-16 h-16 bg-[#F8F9FA] flex items-center justify-center mx-auto mb-6">
                    <IconComponent className="text-[#0A2A5E]" size={32} />
                  </div>
                  <h4 className="text-lg font-semibold text-[#0A2A5E] mb-3">{value.title}</h4>
                  <p className="text-gray-600 text-sm leading-relaxed">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Industries We Serve */}
      <section className="le-section bg-white" data-testid="about-industries">
        <div className="le-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-sm font-semibold text-[#0A2A5E] uppercase tracking-wider mb-4 block">
                Industry Experience
              </span>
              <h2 className="le-heading-2 mb-6">Industries We Serve</h2>
              <p className="text-gray-600 mb-8 leading-relaxed">
                Our attorneys have deep experience serving clients across a diverse range of industries. This breadth of knowledge allows us to understand the unique challenges and regulatory environments facing different sectors, providing more effective and targeted legal counsel.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {industries.map((industry, index) => {
                  const IconComponent = industry.icon;
                  return (
                    <div 
                      key={index} 
                      className="flex items-center gap-3 p-4 bg-[#F8F9FA] border border-[#E2E8F0]"
                      data-testid={`industry-${index}`}
                    >
                      <IconComponent className="text-[#0A2A5E]" size={20} />
                      <span className="text-gray-700 font-medium">{industry.name}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            <div 
              className="w-full h-[400px] bg-cover bg-center"
              style={{
                backgroundImage: `url('https://images.unsplash.com/photo-1760346546771-a81d986459ff?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMHRlYW0lMjBjb2xsYWJvcmF0aW9uJTIwb2ZmaWNlJTIwdGFibGUlMjBkaXNjdXNzaW9uJTIwcHJvZmVzc2lvbmFsfGVufDB8fHx8MTc3Mzk0NDM0MHww&ixlib=rb-4.1.0&q=85')`
              }}
            ></div>
          </div>
        </div>
      </section>

      {/* Our Commitment - NEW SECTION */}
      <section className="le-section bg-[#F8F9FA]" data-testid="about-commitment">
        <div className="le-container">
          <div className="text-center mb-16">
            <span className="text-sm font-semibold text-[#0A2A5E] uppercase tracking-wider mb-4 block">
              Client Promise
            </span>
            <h2 className="le-heading-2 mb-4">Our Commitment to You</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              When you work with Legal Emphasis, you can expect a client experience built on these core commitments.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {commitments.map((commitment, index) => (
              <div 
                key={index} 
                className="bg-white p-8 border border-[#E2E8F0] flex gap-4"
                data-testid={`commitment-${index}`}
              >
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-[#0A2A5E] flex items-center justify-center">
                    <CheckCircle className="text-white" size={20} />
                  </div>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-[#0A2A5E] mb-2">{commitment.title}</h4>
                  <p className="text-gray-600 text-sm leading-relaxed">{commitment.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="le-section bg-white" data-testid="about-why-choose">
        <div className="le-container">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="le-heading-2 mb-6">Why Choose Legal Emphasis?</h2>
            <div className="space-y-6 text-gray-600 leading-relaxed text-left">
              <div className="flex gap-4">
                <div className="w-8 h-8 bg-[#0A2A5E] flex-shrink-0 flex items-center justify-center text-white text-sm font-bold">1</div>
                <div>
                  <h4 className="font-semibold text-[#0A2A5E] mb-1">Experienced Team</h4>
                  <p>Our attorneys have decades of combined experience handling complex legal matters for businesses across diverse industries.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-8 h-8 bg-[#0A2A5E] flex-shrink-0 flex items-center justify-center text-white text-sm font-bold">2</div>
                <div>
                  <h4 className="font-semibold text-[#0A2A5E] mb-1">Business-Focused Approach</h4>
                  <p>We understand that legal matters are part of broader business objectives. Our advice is practical and aligned with your goals.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-8 h-8 bg-[#0A2A5E] flex-shrink-0 flex items-center justify-center text-white text-sm font-bold">3</div>
                <div>
                  <h4 className="font-semibold text-[#0A2A5E] mb-1">Responsive Service</h4>
                  <p>We pride ourselves on being accessible and responsive. Your calls and emails are answered promptly by knowledgeable professionals.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-8 h-8 bg-[#0A2A5E] flex-shrink-0 flex items-center justify-center text-white text-sm font-bold">4</div>
                <div>
                  <h4 className="font-semibold text-[#0A2A5E] mb-1">Transparent Billing</h4>
                  <p>We provide clear fee arrangements upfront and keep you informed about costs. No surprises, just straightforward billing.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section 
        className="relative py-24 md:py-32"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1681505526188-b05e68c77582?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHw0fHxidXNpbmVzcyUyMHByb2Zlc3Npb25hbHMlMjBzaGFraW5nJTIwaGFuZHMlMjBkZWFsJTIwYWdyZWVtZW50JTIwd2lkZSUyMGFuZ2xlfGVufDB8fHx8MTc3Mzk0NTE2MHww&ixlib=rb-4.1.0&q=85')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
        data-testid="final-cta-section"
      >
        <div className="absolute inset-0 bg-[#0A2A5E]/80"></div>
        <div className="le-container relative z-10 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Schedule Your Consultation Today
          </h2>
          <p className="text-lg text-gray-200 mb-8 max-w-2xl mx-auto">
            Let our experienced attorneys guide you through your legal challenges with confidence.
          </p>
          <button 
            onClick={onOpenLeadPopup}
            className="bg-white text-[#0A2A5E] px-10 py-4 font-semibold uppercase tracking-wider 
                       hover:bg-gray-100 transition-all duration-300 inline-flex items-center justify-center gap-2 btn-lift"
            data-testid="final-cta-btn"
          >
            Get Legal Help
            <ArrowRight size={18} />
          </button>
        </div>
      </section>

    </div>
  );
};
